import React, { createContext, useContext, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { translations, Language } from "./translations";

type TranslationType = typeof translations.it | typeof translations.en;

interface LanguageContextType {
  language: Language;
  t: TranslationType;
  switchLanguage: (lang: Language) => void;
  getLocalizedPath: (path: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
  defaultLang: Language;
}

export const LanguageProvider = ({ children, defaultLang }: LanguageProviderProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Use the defaultLang passed from the route
  const language: Language = defaultLang;
  const t = translations[language];

  const switchLanguage = (newLang: Language) => {
    const currentPath = location.pathname;
    
    // Remove current language prefix if exists
    let pathWithoutLang = currentPath;
    if (currentPath.startsWith("/it") || currentPath.startsWith("/en")) {
      pathWithoutLang = currentPath.slice(3) || "/";
    }
    
    // Add new language prefix
    const newPath = `/${newLang}${pathWithoutLang === "/" ? "" : pathWithoutLang}`;
    navigate(newPath + location.search + location.hash);
  };

  const getLocalizedPath = (path: string) => {
    if (path.startsWith("http") || path.startsWith("#")) {
      return path;
    }
    return `/${language}${path === "/" ? "" : path}`;
  };

  return (
    <LanguageContext.Provider value={{ language, t, switchLanguage, getLocalizedPath }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
